import styled, { css } from 'styled-components';
import ArrowDownIcon from 'mdi-react/ChevronDownIcon';
import { ThemeType } from '@prisjakt/ui-base/styles/themes/default';
import { Hideable } from '@regular_ui/lib/components/hideable';
import { nudgeUpHideableVariant } from '@prisjakt/customization_prisjakt/variants/hideable';
import { Grow, Variant } from './types';

const borderSize = 1;

const dropdownBorder = (
  props: { theme: ThemeType } & SharedProps,
  transparent: boolean = false
) => {
  return `${borderSize}px solid ${
    transparent
      ? 'transparent'
      : props.theme.dropdowns[props.variant].borderColor.active
  }`;
};

export const sharedButtonStyles = {
  cursor: 'pointer',
};

type SharedProps = {
  expanded: boolean;
  variant: Variant;
};

type DropdownProps = {} & SharedProps;

export const Dropdown = styled.div<DropdownProps>(
  props => css`
    position: relative;
    border: ${dropdownBorder(props)};
    border-bottom: ${props.expanded && props.variant === 'primary'
      ? dropdownBorder(props, true)
      : dropdownBorder(props)};
    border-top-left-radius: ${props.theme.dropdowns[props.variant]
      .borderRadius};
    border-top-right-radius: ${props.theme.dropdowns[props.variant]
      .borderRadius};
    border-bottom-left-radius: ${props.expanded
      ? 0
      : props.theme.dropdowns[props.variant].borderRadius};
    border-bottom-right-radius: ${props.expanded
      ? 0
      : props.theme.dropdowns[props.variant].borderRadius};
    max-height: 40px;

    color: ${props.theme.icons.tertiary};

    ${props.variant === 'secondary' &&
      css`
        svg,
        path {
          fill: currentColor;
        }

        border-color: transparent;

        &:hover {
          & > button {
            color: ${props.theme.icons.callToAction};
          }
        }

        & > button {
          color: ${props.expanded ? props.theme.icons.callToAction : undefined};
        }
      `};

    ${props.variant !== 'secondary' &&
      css`
        border-color: ${props.expanded
          ? props.theme.dropdowns[props.variant].borderColor.active
          : props.theme.dropdowns[props.variant].borderColor.inactive};

        background: ${props.expanded
          ? props.theme.dropdowns[props.variant].bg
          : 'transparent'};
      `};

    .dropdown-toggle-element {
      &:hover {
        color: ${p => p.theme.buttons.link.textHover};
      }
    }
  `
);

export const DropdownHideable = styled(Hideable).attrs({
  // NOTE: Casting as `any` to suppress the TS error. Should be fixed when we have time
  variant: nudgeUpHideableVariant as any,
})`
  position: relative;
  z-index: 11;
`;

type DropdownContentProps = {
  grow: Grow;
} & SharedProps;

export const DropdownContent = styled.div<DropdownContentProps>(
  props => css`
    display: block; /* display flex + absolute position breaks in IE11 */
    position: absolute;
    border: ${dropdownBorder(props)};
    border-top: ${props.expanded && props.variant === 'primary'
      ? dropdownBorder(props, true)
      : dropdownBorder(props)};
    background: ${props.theme.dropdowns[props.variant].bg};
    border-top-left-radius: ${props.expanded
      ? 0
      : props.theme.dropdowns[props.variant].borderRadius};
    border-top-right-radius: ${props.expanded
      ? 0
      : props.theme.dropdowns[props.variant].borderRadius};
    border-bottom-left-radius: ${props.theme.dropdowns[props.variant]
      .borderRadius};
    border-bottom-right-radius: ${props.theme.dropdowns[props.variant]
      .borderRadius};

    ${props.grow === 'right' &&
      css`
        top: calc(100% - 2px);
        left: -${borderSize}px;
      `};

    ${props.grow === 'left' &&
      css`
        top: calc(100% - 2px);
        right: -${borderSize}px;
      `};

    ${props.grow === 'none' &&
      css`
        top: calc(100% - 2px);
        left: -${borderSize}px;
        right: -${borderSize}px;
      `};
  `
);

const arrowStyles = css`
  margin-left: auto;
`;

type ArrowProps = {
  expanded: number;
};

export const Arrow = styled(ArrowDownIcon)<ArrowProps>`
  ${arrowStyles}

  && {
    transform: ${p => (p.expanded ? 'rotate(180deg)' : 'rotate(0)')};
  }
`;
